import { escapeRegExp } from 'lodash';
import { IonText } from '@ionic/react';
import { FormatOptionLabelMeta, components } from 'react-select';
import CreatableSelectAsync from 'react-select/async-creatable';
import CreatableSelectSync from 'react-select/creatable';
import { ISearchableSelectFieldProps, ISelected } from './SearchableSelectField.tsx';
import { SearchIcon } from '../icons/Icons.tsx';

const CustomControl = (props: any) => {
    // simulate ionic label behavior
    const labelText = props.selectProps['data-label'];
    const labelPlacement = props.selectProps['data-labelPlacement'];
    const stacked = (labelPlacement === 'stacked' || props.isFocused || props.hasValue || Boolean(props.selectProps.inputValue));
    const required = props.selectProps['data-required'];
    return (
        <div style={{marginTop: 28}}>
            <IonText
                color="medium"
                style={{
                    zIndex: 1,
                    position: 'absolute',
                    width: stacked ? undefined : '100%',
                    top: stacked ? -12 : 12,
                    fontSize: stacked ? 14 : 17,
                    transition: 'top 0.15s, font-size 0.1s',
                    pointerEvents: 'none'
                }}
            >{labelText}{required ? '*' : ''}</IonText>
            <components.Control {...props} />
        </div>
    );
};

// NOTE: autocapitalize only works with a virtual keyboard,
//  'Desktop' is slightly misleading as this component is used breakpoints sm+
const CustomInput = (props: any) => {
    return (
        <components.Input {...props} autoCapitalize="words" />
    );
};

const CustomDropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            {!props.isDisabled &&
                <SearchIcon size="medium" color="medium" />
            }
            {props.isDisabled &&
                <span />
            }
        </components.DropdownIndicator>
    );
};

const CustomIndicatorSeparator = () => null;

const CustomPlaceholder = (props: any) => {
    const { children, ...rest } = props;
    return props.isFocused ? <components.Placeholder {...rest}><i>{children}</i></components.Placeholder> : null;
};

export const SearchableSelectFieldDesktop = <T,>({
    'data-type': dataType,
    label,
    placeholder,
    required = false,
    allowCustom = false,
    noInputMessage = '',
    readonly = false,
    selected,
    onChange,
    options,
    loadOptions,
}: ISearchableSelectFieldProps<T>) => {

    const adaptValue = (newValue: any) => {
        // for no selection, component returns null or empty label, but we want undefined
        if (!newValue?.label) {
            return undefined;
        }
        // for "enter your own", component returns label in value, but we want undefined
        return { label: newValue.label, value: (newValue.__isNew__ ? undefined : newValue.value) };
    };

    const formatOptionLabel = (data: ISelected<T>, labelMeta: FormatOptionLabelMeta<ISelected<T>>) => {
        if (!labelMeta.inputValue) {
            return (<span data-value={data.value}>{data.label}</span>);
        }
        const escapedInput = escapeRegExp(labelMeta.inputValue);
        const formatted = data.label.replace(
            new RegExp(escapedInput, 'gi'),
            highlighted => `<strong>${highlighted}</strong>`
        );
        return (
            <span data-value={data.value} dangerouslySetInnerHTML={{ __html: formatted }} />
        );
    };

    const formatCreateLabel = (inputValue: string) => {
        return !inputValue && noInputMessage ? <i>{noInputMessage}</i> : `${inputValue} (enter your own)`;
    };

    const isValidNewOption = (input: string, value: any, _options: readonly any[]) => {
        // show "enter your own" if input doesn't match any of the options
        return allowCustom && !_options.some(o => o.label.toLowerCase() === input.trim().toLowerCase());
    };

    const CreatableSelect = loadOptions ? CreatableSelectAsync : CreatableSelectSync;

    return (
        <div style={{width: '100%'}}>
            <CreatableSelect
                data-type={dataType}
                data-required={required}
                data-label={label}
                data-labelPlacement={readonly ? 'stacked' : 'floating'}
                required={required}
                isDisabled={readonly}
                placeholder={placeholder}
                value={selected}
                onChange={v => onChange(adaptValue(v))}
                blurInputOnSelect={false}
                cacheOptions
                options={options}
                loadOptions={loadOptions}
                formatOptionLabel={formatOptionLabel}
                isClearable
                backspaceRemovesValue={false}
                formatCreateLabel={formatCreateLabel}
                isValidNewOption={isValidNewOption}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                minMenuHeight={200}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary: 'var(--ion-color-primary)',
                        primary75: 'rgba(var(--ion-color-primary-rgb), 0.75)',
                        primary50: 'rgba(var(--ion-color-primary-rgb), 0.70)',
                        primary25: 'rgba(var(--ion-color-primary-rgb), 0.25)',
                        neutral20: 'var(--ion-color-step-300)',
                        neutral30: 'var(--ion-color-step-700)',
                        danger: 'var(--ion-color-danger)',
                        dangerLight: 'var(--ion-color-danger-tint)'
                    },
                })}
                components={{
                    // note: react-select docs advise against defining these inline
                    // (see https://react-select.com/components#defining-components)
                    Control: CustomControl,
                    Input: CustomInput,
                    DropdownIndicator: CustomDropdownIndicator,
                    IndicatorSeparator: CustomIndicatorSeparator,
                    Placeholder: CustomPlaceholder
                }}
                styles={{
                    control: provided => ({
                        ...provided,
                        cursor: 'pointer',
                        border: 'none',
                        boxShadow: 'none',
                        outline: 'none',
                        background: 'initial!important',
                        '&:hover': { border: 'none' }
                    }),
                    valueContainer: provided => ({
                        ...provided,
                        paddingLeft: 0,
                        cursor: 'text'
                    }),
                    singleValue: provided => ({
                        ...provided,
                        color: 'var(--ion-text-color)'
                    })
                }}
            />
        </div>
    );
};
